// ============================================
// Stock Imports Hooks - TanStack Query
// For Order Proposals, Pending Orders, Receipt Orders
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { 
  StockImport, 
  StockImportListParams, 
  CreateStockImportRequest,
  UpdateStockImportRequest,
  CompleteStockImportRequest,
  StockImportStatus 
} from '@conai/shared-types';
import { useStockImportsService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';
import { useI18n } from '../i18n';
import { getApiErrorMessage, translateOrFallback } from '../utils/apiError';

// ============================================
// Toast dùng chung
// ============================================

/**
 * `translateOrFallback` giữ lại câu tiếng Anh cũ khi key mới chưa được merge
 * vào locale — `t` trả về CHÍNH KEY lúc thiếu bản dịch, đưa thẳng ra toast thì
 * người dùng đọc được chuỗi `toastStockImportApproved`.
 */
const translateOr = translateOrFallback;

/**
 * Bug #160: mọi mutation đổi trạng thái phiếu nhập trước đây chỉ có `onSuccess`.
 * Request hỏng — 400 thiếu lý do, 403 sai quyền, sai trạng thái — đều rơi vào im
 * lặng: không toast, không đổi gì trên màn hình.
 *
 * Bản trước của handler này kiểm `error instanceof Error`, mà lỗi của
 * api-client là OBJECT thuần — điều kiện luôn sai, nên mọi lỗi đều hiện đúng
 * một câu chung chung tiếng Anh, che mất lý do server đã nói rõ.
 *
 * Giờ đọc qua `getApiErrorMessage`: `error_code`/`error_details` khi có,
 * `message` khi không. Nhóm exception nghiệp vụ của stock_imports hiện còn bị
 * catch-all của BE bọc lại nên chưa có `error_code` — lúc BE gỡ ra thì câu
 * thông báo TỰ chuyển sang bản đã dịch, không phải sửa lại chỗ nào ở FE.
 */
function useStockImportErrorToast() {
  const { t } = useI18n();

  return (fallbackKey: string, fallbackText: string) => (error: unknown) => {
    toast.error(
      getApiErrorMessage(error, t, { fallback: translateOr(t, fallbackKey, fallbackText) })
    );
  };
}

// ============================================
// Extended query keys for stock imports
// ============================================

export const stockImportQueryKeys = {
  ...queryKeys.stockImports,
  stats: (storeId?: string) => [...queryKeys.stockImports.all, 'stats', storeId] as const,
  byStatus: (status: StockImportStatus) => [...queryKeys.stockImports.all, 'status', status] as const,
};

// ============================================
// List Hook
// ============================================

/**
 * Hook to list stock imports with pagination and filters
 */
export function useStockImports(filters: StockImportListParams = {}) {
  const stockImportsService = useStockImportsService();

  return useQuery({
    queryKey: queryKeys.stockImports.list(filters as Record<string, unknown>),
    queryFn: () => stockImportsService.list(filters),
  });
}

/**
 * Hook to list stock imports by store
 */
export function useStockImportsByStore(storeId: string, filters: Omit<StockImportListParams, 'store'> = {}) {
  const stockImportsService = useStockImportsService();

  return useQuery({
    queryKey: queryKeys.stockImports.list({ ...filters, store: storeId }),
    queryFn: () => stockImportsService.listByStore(storeId, filters),
    enabled: !!storeId,
  });
}

// ============================================
// Detail Hook
// ============================================

/**
 * Hook to get a single stock import by ID
 */
export function useStockImport(id: string, options?: { enabled?: boolean }) {
  const stockImportsService = useStockImportsService();

  return useQuery({
    queryKey: queryKeys.stockImports.detail(id),
    queryFn: () => stockImportsService.getById(id),
    enabled: options?.enabled !== false && !!id,
  });
}

// ============================================
// Create Mutation
// ============================================

/**
 * Hook to create a new stock import
 */
export function useCreateStockImport() {
  const stockImportsService = useStockImportsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (data: CreateStockImportRequest) => stockImportsService.create(data),
    onSuccess: (newImport) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stockImports.lists() });
      // Bug #158: tuỳ cấu hình, BE có thể ghi nhận tồn kho ngay khi tạo phiếu
      // (hoặc tạo sẵn record inventory cho sản phẩm mới). Nếu không dọn cache
      // inventory ở đây thì trang Inventory không thấy sản phẩm vừa nhập.
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      toast.success(
        translateOr(t, 'toastStockImportCreated', `Created import order ${newImport.import_number}`, {
          number: newImport.import_number,
        })
      );
    },
  });
}

// ============================================
// Update Mutation
// ============================================

/**
 * Hook to update a stock import
 */
export function useUpdateStockImport() {
  const stockImportsService = useStockImportsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const toastActionError = useStockImportErrorToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStockImportRequest }) =>
      stockImportsService.update(id, data),
    onSuccess: (updatedImport) => {
      queryClient.setQueryData(queryKeys.stockImports.detail(updatedImport.id), updatedImport);
      queryClient.invalidateQueries({ queryKey: queryKeys.stockImports.lists() });
      toast.success(
        translateOr(t, 'toastStockImportUpdated', `Updated import order ${updatedImport.import_number}`, {
          number: updatedImport.import_number,
        })
      );
    },
    onError: toastActionError('stockImportUpdateFailed', 'Failed to update import'),
  });
}

// ============================================
// Status Action Mutations
// ============================================

/**
 * Hook to submit a draft stock import for approval
 *
 * Dùng câu của app, `result.message` của BE chỉ là fallback.
 *
 * ĐÍNH CHÍNH (BE 26/08): trước đây ghi ở đây là `message` không đi qua
 * Accept-Language — SAI. `LocaleMiddleware` đã bật, `store-admin` có gửi header
 * (`config/api.ts` → `getLanguage`), và DRF ship sẵn catalogue cho pl/uk/vi/en
 * nên message field chuẩn (`required`, `blank`, `invalid`...) đã được dịch từ
 * lâu. Vẫn ưu tiên câu của app vì hai lý do còn nguyên giá trị: (1) message BE
 * TỰ VIẾT trong code chưa có bản dịch `uk`; (2) câu của app bám ngữ cảnh màn
 * hình ("Không gửi được phiếu nhập") trong khi câu BE mô tả vi phạm kỹ thuật.
 * Nguyên tắc: câu chữ để hiển thị lấy của app, `error_details[].code` để rẽ
 * nhánh — không phụ thuộc tiến độ dịch bên nào.
 */
export function useSubmitStockImport() {
  const stockImportsService = useStockImportsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const toastActionError = useStockImportErrorToast();

  return useMutation({
    mutationFn: (id: string) => stockImportsService.submit(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stockImports.all });
      toast.success(
        translateOr(t, 'toastStockImportSubmitted', result.message || 'Submitted for approval')
      );
    },
    onError: toastActionError('stockImportSubmitFailed', 'Failed to submit import for approval'),
  });
}

/**
 * Hook to approve a stock import
 */
export function useApproveStockImport() {
  const stockImportsService = useStockImportsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const toastActionError = useStockImportErrorToast();

  return useMutation({
    mutationFn: (id: string) => stockImportsService.approve(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stockImports.all });
      toast.success(translateOr(t, 'toastStockImportApproved', result.message || 'Order approved'));
    },
    onError: toastActionError('stockImportApproveFailed', 'Failed to approve import'),
  });
}

/**
 * Hook to reject a stock import
 */
export function useRejectStockImport() {
  const stockImportsService = useStockImportsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const toastActionError = useStockImportErrorToast();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      stockImportsService.reject(id, reason),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stockImports.all });
      toast.success(translateOr(t, 'toastStockImportRejected', result.message || 'Order rejected'));
    },
    onError: toastActionError('stockImportRejectFailed', 'Failed to reject import'),
  });
}

/**
 * Hook to start receiving goods for a stock import
 */
export function useStartReceivingStockImport() {
  const stockImportsService = useStockImportsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const toastActionError = useStockImportErrorToast();

  return useMutation({
    mutationFn: (id: string) => stockImportsService.startReceiving(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stockImports.all });
      // Bug #158: nhận hàng bắt đầu ghi số lượng thực nhận vào kho.
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      toast.success(
        translateOr(t, 'toastStockImportReceivingStarted', result.message || 'Started receiving goods')
      );
    },
    onError: toastActionError('stockImportStartReceivingFailed', 'Failed to start receiving'),
  });
}

/**
 * Hoàn tất phiếu nhập — GHI SỐ LƯỢNG NHẬN TRƯỚC, rồi mới complete.
 *
 * Bug #158: bản cũ chỉ gọi `complete/` kèm `items`. Nhưng `CompleteSerializer`
 * của BE chỉ có `idempotency_key` — DRF vứt `items` im lặng, mọi dòng giữ
 * `received_quantity = 0`, `complete/` bỏ qua hết và phiếu vẫn thành COMPLETED
 * mà tồn kho không tăng một đơn vị nào. Ba tầng nuốt lỗi liên tiếp.
 *
 * Số lượng thực nhận phải đi qua `bulk-receive/`. Sau khi BE siết, `complete/`
 * trả 400 nếu không dòng nào nhận được gì — nên thiếu bước này là hỏng ồn ào
 * thay vì hỏng âm thầm, nhưng vẫn là hỏng.
 */
export function useCompleteStockImport() {
  const stockImportsService = useStockImportsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const toastActionError = useStockImportErrorToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CompleteStockImportRequest }) => {
      const entries = (data.items ?? []).filter((item) => item.received_quantity > 0);

      // Bug #159 — BE chốt 19/08/2026: `bulk-receive/` giờ trả 400 kèm
      // `errors.unmatched` (danh sách định danh không khớp) và rollback toàn bộ
      // payload; phiếu không ở `RECEIVING` cũng 400. `complete/` trả 400 nếu
      // không dòng nào có `received_quantity > 0` và không chuyển COMPLETED.
      //
      // Nghĩa là bước "đọc lại phiếu để xác nhận" mà FE tự thêm đã thừa — lỗi
      // nào cũng ném ra từ chính request, không còn 200-mà-không-ghi. Đã gỡ,
      // đổi lại một request mỗi lần hoàn tất.
      if (entries.length > 0) {
        await stockImportsService.bulkReceive(id, { items: entries });
      }

      // `complete/` không đọc `items` — chỉ gửi phần nó thực sự dùng.
      return stockImportsService.complete(id, {
        ...(data.received_date ? { received_date: data.received_date } : {}),
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stockImports.all });
      // Bug #146: hoàn tất nhập kho làm thay đổi tồn kho — phải invalidate cache
      // inventory, nếu không trang Inventory vẫn hiển thị số cũ (staleTime 60s
      // + refetchOnMount:false).
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      toast.success(translateOr(t, 'toastStockImportCompleted', result.message || 'Order completed'));
    },
    // Mutation này gồm hai request; lỗi có thể tới từ `bulk-receive/` (400 kèm
    // `errors.unmatched` — dạng DỮ LIỆU, BE cố ý không sinh `error_details`)
    // hoặc từ `complete/`. `getApiErrorMessage` tự rơi về `message` ở ca đầu.
    onError: toastActionError('stockImportCompleteFailed', 'Failed to complete import'),
    // Mutation này gồm NHIỀU bước ghi (bulk-receive rồi complete) và không
    // idempotent. Mặc định toàn cục `retry: 1` sẽ chạy lại bulk-receive sau khi
    // bước sau thất bại — cộng tồn kho hai lần. Không retry.
    retry: 0,
  });
}

/**
 * Hook to cancel a stock import
 */
export function useCancelStockImport() {
  const stockImportsService = useStockImportsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const toastActionError = useStockImportErrorToast();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      stockImportsService.cancel(id, reason),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stockImports.all });
      toast.success(translateOr(t, 'toastStockImportCancelled', result.message || 'Order cancelled'));
    },
    onError: toastActionError('stockImportCancelFailed', 'Failed to cancel import'),
  });
}

// ============================================
// Bulk Actions
// ============================================

/**
 * Hook to approve multiple stock imports
 */
export function useBulkApproveStockImports() {
  const approveStockImport = useApproveStockImport();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map(id => approveStockImport.mutateAsync(id))
      );
      return results;
    },
    onSuccess: (results) => {
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;
      
      queryClient.invalidateQueries({ queryKey: queryKeys.stockImports.all });
      
      if (failCount === 0) {
        toast.success(
          translateOr(t, 'toastStockImportsBulkApproved', `Approved ${successCount} orders`, {
            count: successCount,
          })
        );
      } else {
        // Lỗi của TỪNG phiếu đã được `useApproveStockImport` toast riêng kèm lý
        // do; ở đây chỉ tổng kết số lượng.
        toast.error(
          translateOr(
            t,
            'toastStockImportsBulkApprovedPartial',
            `Approved ${successCount} orders, ${failCount} failed`,
            { success: successCount, failed: failCount }
          )
        );
      }
    },
  });
}

// ============================================
// Stats Helper
// ============================================

/**
 * Calculate status counts from stock imports list
 */
export function useStockImportStats(storeId?: string) {
  const { data, isLoading, error } = useStockImports({
    store: storeId,
    page_size: 1000, // Get all to calculate stats
  });

  const stats = {
    draft: 0,
    submitted: 0,
    approved: 0,
    receiving: 0,
    completed: 0,
    rejected: 0,
    total: 0,
    totalValue: 0,
  };

  if (data?.results) {
    data.results.forEach((item: StockImport) => {
      stats.total++;
      stats.totalValue += parseFloat(item.total_cost || '0');
      
      switch (item.status) {
        case 'DRAFT':
          stats.draft++;
          break;
        case 'SUBMITTED':
          stats.submitted++;
          break;
        case 'APPROVED':
          stats.approved++;
          break;
        case 'RECEIVING':
          stats.receiving++;
          break;
        case 'COMPLETED':
          stats.completed++;
          break;
        case 'REJECTED':
          stats.rejected++;
          break;
      }
    });
  }

  return { stats, isLoading, error };
}
